import { typedApi } from "@/api/typed-client";
import { useMutation } from "@tanstack/react-query";

export interface StartTrainingBody {
  project_id: string;
  dataset_id: string;
  model_name: string;
  task_type: string;
  label: string | null;
  training_config: Record<string, unknown>;
  target_model_id?: string | null;
}

export interface StartTrainingResponse {
  model_id: string;
  revision_id: string;
  revision_no: number;
  job_id: string;
}

export class StartTrainingError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly alternatives?: string[],
    public readonly fieldErrors?: Array<{ field: string; msg: string }>,
  ) {
    super(message);
  }
}

export function useStartTraining() {
  return useMutation<StartTrainingResponse, StartTrainingError, StartTrainingBody>({
    mutationFn: async (body) => {
      const { data, error, response } = await typedApi.POST("/v1/jobs/training", {
        // biome-ignore lint/suspicious/noExplicitAny: training_config is union-of-families
        body: body as any,
      });
      if (error) {
        const status = response?.status ?? 500;
        // biome-ignore lint/suspicious/noExplicitAny: error shape varies
        const err = error as any;
        if (status === 409 && err?.error === "capability_mismatch") {
          throw new StartTrainingError(
            409,
            "capability_mismatch",
            err.message ?? "Mode not available on current hardware",
            err.details?.alternatives ?? [],
          );
        }
        if (status === 400 && err?.detail) {
          const fieldErrors = Array.isArray(err.detail)
            ? err.detail.map((d: { loc?: string[]; msg?: string }) => ({
                field: d.loc?.slice(1).join(".") ?? "input",
                msg: d.msg ?? "invalid",
              }))
            : [];
          throw new StartTrainingError(
            400,
            "validation_error",
            "Invalid submission",
            undefined,
            fieldErrors,
          );
        }
        throw new StartTrainingError(
          status,
          "unknown",
          err?.message ?? "Training submission failed",
        );
      }
      // Justified: training response is {[key:string]:unknown} in openapi schema
      return data as unknown as StartTrainingResponse;
    },
  });
}
